import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useThree } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

export interface Measurement {
  id: number;
  start: THREE.Vector3;
  end: THREE.Vector3;
  distance: number;
}

export interface MeasurementSystemHandle {
  clearAll: () => void;
  getMeasurementCount: () => number;
}

interface MeasurementSystemProps {
  enabled: boolean;
  /** Ref to the group containing all pickable model meshes */
  modelGroupRef: React.RefObject<THREE.Group | null>;
  /** Called when drawing state changes (to disable orbit controls) */
  onDrawingChange?: (isDrawing: boolean) => void;
  /** Called when the number of completed measurements changes */
  onMeasurementCountChange?: (count: number) => void;
}

/** Renders a single completed or in-progress measurement line with a label */
function MeasurementLine({
  start,
  end,
  distance,
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  distance: number;
}) {
  const midpoint = useMemo(
    () => new THREE.Vector3().lerpVectors(start, end, 0.5),
    [start, end],
  );

  const points = useMemo(() => [start, end], [start, end]);

  const geometry = useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(points);
  }, [points]);

  const material = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.75,
      depthTest: false,
    });
  }, []);

  // Small sphere at each endpoint
  const sphereGeometry = useMemo(() => new THREE.SphereGeometry(0.3, 8, 8), []);
  const sphereMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        depthTest: false,
      }),
    [],
  );

  const lineObject = useMemo(() => {
    return new THREE.LineSegments(geometry, material);
  }, [geometry, material]);

  return (
    <group renderOrder={999}>
      <primitive object={lineObject} renderOrder={999} />
      <mesh
        geometry={sphereGeometry}
        material={sphereMaterial}
        position={start}
        renderOrder={999}
      />
      <mesh
        geometry={sphereGeometry}
        material={sphereMaterial}
        position={end}
        renderOrder={999}
      />
      {distance > 0.01 && (
        <Html
          position={midpoint}
          center
          zIndexRange={[100, 0]}
          style={{ pointerEvents: 'none' }}
        >
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '11px',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: '#ffffff',
              padding: '2px 6px',
              borderRadius: '3px',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            {distance.toFixed(2)}mm
          </div>
        </Html>
      )}
    </group>
  );
}

export const MeasurementSystem = forwardRef<
  MeasurementSystemHandle,
  MeasurementSystemProps
>(function MeasurementSystem(
  { enabled, modelGroupRef, onDrawingChange, onMeasurementCountChange },
  ref,
) {
  const { camera, gl } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());

  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<THREE.Vector3 | null>(null);
  const [currentEnd, setCurrentEnd] = useState<THREE.Vector3 | null>(null);
  const nextId = useRef(0);

  // Expose imperative handle for clearing measurements
  useImperativeHandle(
    ref,
    () => ({
      clearAll: () => {
        setMeasurements([]);
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentEnd(null);
      },
      getMeasurementCount: () => measurements.length,
    }),
    [measurements.length],
  );

  // Notify parent about measurement count changes
  useEffect(() => {
    onMeasurementCountChange?.(measurements.length);
  }, [measurements.length, onMeasurementCountChange]);

  // Notify parent about drawing state changes
  useEffect(() => {
    onDrawingChange?.(isDrawing);
  }, [isDrawing, onDrawingChange]);

  // Cancel in-progress drawing when measurement mode is toggled off
  useEffect(() => {
    if (!enabled) {
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentEnd(null);
    }
  }, [enabled]);

  // Gather all pickable meshes from the model group
  const getPickableObjects = useCallback((): THREE.Mesh[] => {
    const meshes: THREE.Mesh[] = [];
    if (modelGroupRef.current) {
      modelGroupRef.current.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          meshes.push(child as THREE.Mesh);
        }
      });
    }
    return meshes;
  }, [modelGroupRef]);

  // Raycast against model meshes
  const raycast = useCallback(
    (event: PointerEvent): THREE.Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);
      const pickable = getPickableObjects();
      if (pickable.length === 0) return null;

      const intersects = raycaster.current.intersectObjects(pickable, false);
      if (intersects.length > 0) {
        return intersects[0].point.clone();
      }
      return null;
    },
    [camera, gl, getPickableObjects],
  );

  // Click handler
  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      if (!enabled) return;
      // Only respond to left click
      if (event.button !== 0) return;

      const point = raycast(event);
      if (!point) return;

      if (!isDrawing) {
        // Start a new measurement
        setStartPoint(point);
        setCurrentEnd(point.clone());
        setIsDrawing(true);
      } else {
        // Finish the measurement
        if (startPoint) {
          const distance = startPoint.distanceTo(point);
          setMeasurements((prev) => [
            ...prev,
            {
              id: nextId.current++,
              start: startPoint,
              end: point,
              distance,
            },
          ]);
        }
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentEnd(null);
      }
    },
    [enabled, isDrawing, startPoint, raycast],
  );

  // Mouse move handler (update the in-progress line endpoint)
  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!enabled || !isDrawing) return;

      const point = raycast(event);
      if (point) {
        setCurrentEnd(point);
      }
    },
    [enabled, isDrawing, raycast],
  );

  // Right-click to cancel in-progress measurement
  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      if (!enabled || !isDrawing) return;
      event.preventDefault();
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentEnd(null);
    },
    [enabled, isDrawing],
  );

  // Escape to cancel, Delete/Backspace to clear all measurements
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      if (event.key === 'Escape' && isDrawing) {
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentEnd(null);
      }

      if ((event.key === 'Delete' || event.key === 'Backspace') && !isDrawing) {
        setMeasurements([]);
      }
    },
    [enabled, isDrawing],
  );

  // Set cursor style when measurement mode is enabled
  useEffect(() => {
    if (enabled) {
      gl.domElement.style.cursor = 'crosshair';
    } else {
      gl.domElement.style.cursor = '';
    }
    return () => {
      gl.domElement.style.cursor = '';
    };
  }, [enabled, gl]);

  // Attach event listeners
  useEffect(() => {
    const canvas = gl.domElement;
    if (!enabled) return;

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    enabled,
    gl,
    handlePointerDown,
    handlePointerMove,
    handleContextMenu,
    handleKeyDown,
  ]);

  // Current distance for in-progress line
  const currentDistance =
    startPoint && currentEnd ? startPoint.distanceTo(currentEnd) : 0;

  return (
    <group>
      {/* Completed measurements */}
      {measurements.map((m) => (
        <MeasurementLine
          key={m.id}
          start={m.start}
          end={m.end}
          distance={m.distance}
        />
      ))}

      {/* In-progress measurement */}
      {isDrawing && startPoint && currentEnd && (
        <MeasurementLine
          start={startPoint}
          end={currentEnd}
          distance={currentDistance}
        />
      )}
    </group>
  );
});
